import React from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ChevronRight, ArrowBack } from "@mui/icons-material";

type Step = { icon: string; label: string };

type CustomStepperProps = {
  onClickHeader: () => void;
  activeStep: number;
  completedSteps: number[];
  onStepClick?: (index: number) => void;
  progress: number;
  mode: "create" | "edit";
};

/** ⚡ Stage Card MVP: four steps  */
const steps: Step[] = [
  { icon: "📄", label: "Basic Info" }, // title, venueSize, rank
  { icon: "💰", label: "Scope & Pricing" }, // price, observer, instant
  { icon: "🖼️", label: "Media & Branding" }, // thumbnail, preview video   // price, observer, instant
  { icon: "🚀", label: "Review & Publish" }, // preview + submit
];

const CustomStepper: React.FC<CustomStepperProps> = ({
  activeStep,
  completedSteps,
  onStepClick,
  progress,
  onClickHeader,
  mode,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box>
      {/* ——— Header ——— */}
      <Stack direction="row" alignItems="center" spacing={1.5} px={2}>
        <IconButton size="small" onClick={onClickHeader}>
          <ArrowBack />
        </IconButton>
        <Typography
          variant="h5"
          fontWeight={700}
          fontFamily="Lexend"
          sx={{ color: "#333", pt: 3, pb: 2 }}
        >
          {mode === "edit" ? "Edit Stage Card" : "Create Stage Card"}
        </Typography>
      </Stack>

      <Divider />

      {/* ——— Step Icons Row ——— */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        py={1.5}
        sx={{
          overflowX: "auto",
          whiteSpace: "nowrap",
          maxWidth: `calc(100vw - ${isMobile ? 64 : 360}px)`,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="nowrap"
        >
          {steps.map((step, index) => {
            const isActive = index === activeStep;
            const isCompleted = completedSteps.includes(index);
            const isClickable = isCompleted;

            const iconBg = isActive
              ? "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)"
              : isCompleted
                ? "#10B981"
                : "#F1F5F9";

            const iconColor = isActive || isCompleted ? "#fff" : "#6B7280";
            const iconContent = isCompleted ? "✔️" : step.icon;

            const labelColor = isActive ? "#6366F1" : "#6B7280";

            return (
              <Stack
                key={index}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  flexShrink: 0,
                  cursor: isClickable ? "pointer" : "default",
                  opacity: isClickable || isActive ? 1 : 0.5,
                }}
                onClick={() => isClickable && onStepClick?.(index)}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: iconBg,
                    color: iconColor,
                    fontSize: 16,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {iconContent}
                </Box>
                <Typography
                  fontSize={14}
                  fontWeight={600}
                  color={labelColor}
                  fontFamily='"Lexend", sans-serif'
                  noWrap
                >
                  {step.label}
                </Typography>
                {index < steps.length - 1 && (
                  <ChevronRight fontSize="small" sx={{ color: "#D1D5DB" }} />
                )}
              </Stack>
            );
          })}
        </Stack>

        {/* ——— Progress Bar ——— */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          ml={2}
          flexShrink={0}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            color="text.secondary"
            whiteSpace="nowrap"
          >
            Completion Rate: {progress}%
          </Typography>
          <Box
            sx={{
              width: 100,
              height: 6,
              backgroundColor: "#E5E7EB",
              borderRadius: 999,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${progress}%`,
                height: "100%",
                background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
              }}
            />
          </Box>
        </Stack>
      </Stack>

      <Divider />
    </Box>
  );
};

export default CustomStepper;
